import * as React from 'react';
import { DefaultButton, IDetailsColumnFieldProps, IRenderFunction, Stack } from '@fluentui/react';
import { apiFetch } from '../../lib/api-client';
import { Process, Service } from '../../interfaces/Service';
import {
    AgencyModelPickerItem,
    AgencyModelsSettingResult,
    AgentTaskFlameGraphNode,
    AgentTaskFlameGraphResult,
    AgentTaskLifecycleStatus,
    AgentTaskListRowModel,
    AgentTaskSnapshotSummary,
    AgentTaskTaskSummary,
    AgentTaskWorkItemSummary,
    CallGraphDownloadFormat,
    JOB_RUNTIME_SUBSTITUTION_OPTIONS,
    JobModelConfiguration,
    RepairAgentTaskPullRequestResult,
    AGENT_TASK_LIFECYCLE_STATUSES,
    SNIPPET_SYNTAX_REGEX,
    TASK_LIST_SORT_KEYS,
    TASK_TYPES,
    TaskListSortDirection,
    TaskListSortKey,
} from './types';
import { buildAgentTaskQuestionsPageUrl } from './questionsApi';
import { buildAgentTaskProposedChangesPageUrl } from './proposedChangesApi';

const SUPPORTED_RUNTIME_SUBSTITUTION_TOKENS = new Set(
    JOB_RUNTIME_SUBSTITUTION_OPTIONS.map(option => option.token.toLowerCase()),
);

export function renderCopyableDetailsField(
    props?: IDetailsColumnFieldProps,
    defaultRender?: IRenderFunction<IDetailsColumnFieldProps>,
) {
    const field = defaultRender?.(props);
    if (!React.isValidElement<{ style?: React.CSSProperties }>(field)) {
        return field;
    }

    return React.cloneElement(field, {
        'data-selection-disabled': true,
        style: {
            ...field.props.style,
            userSelect: 'text',
        },
    } as React.HTMLAttributes<HTMLElement>);
}

export type MultipartRequestError = {
    data: unknown;
    status: number;
    error: true;
};

export function isProfileBackedTaskType(taskType: number) {
    return taskType === 0 || taskType === 1;
}

export function isUploadBackedTaskType(taskType: number) {
    return taskType === 2;
}

export function isUnsupportedTaskType(taskType: number) {
    return taskType === 3;
}

export function normalizeMetricName(name?: string) {
    return (name ?? '').replace(/[^a-z0-9]/gi, '').toLowerCase();
}

export function getDerivedMetricName(taskType: number) {
    switch (taskType) {
        case 0:
            return 'OnCpu';
        case 1:
            return 'Memory';
        default:
            return undefined;
    }
}

export function getDerivedMetricId(process: Process | undefined, taskType: number) {
    const requiredMetricName = getDerivedMetricName(taskType);
    if (!requiredMetricName) {
        return undefined;
    }

    const normalizedRequiredMetricName = normalizeMetricName(requiredMetricName);
    return Object.values(process?.metrics ?? {})
        .find(metric => normalizeMetricName(metric.name) === normalizedRequiredMetricName)
        ?.id;
}

export function getTaskTypeLabel(taskType: number) {
    return TASK_TYPES.find(option => option.key === taskType)?.text ?? `Type ${taskType}`;
}

export function normalizeJobModelConfiguration(modelConfiguration?: JobModelConfiguration | null) {
    if (!modelConfiguration) {
        return null;
    }

    const modelId = modelConfiguration.modelId?.trim() ?? '';
    const reasoningEffort = modelConfiguration.reasoningEffort?.trim() ?? '';
    if (!modelId) {
        return null;
    }

    return {
        modelId,
        reasoningEffort: reasoningEffort || null,
        useLongContext: !!modelConfiguration.useLongContext,
    } satisfies JobModelConfiguration;
}

export function parseAgencyModelsSettingResponse(response?: AgencyModelsSettingResult | null) {
    return parseAgencyModelsSettingValue(response?.value);
}

export function parseAgencyModelsSettingValue(value?: string | null): AgencyModelPickerItem[] {
    if (!value?.trim()) {
        return [];
    }

    const parsed = JSON.parse(value) as {
        models?: Array<{
            id?: unknown;
            name?: unknown;
            modelPickerEnabled?: unknown;
            capabilities?: {
                supports?: {
                    reasoningEffort?: unknown;
                };
            };
        }>;
    };

    const models = Array.isArray(parsed.models) ? parsed.models : [];
    return models
        .filter(model => model?.modelPickerEnabled === true)
        .map(model => {
            const reasoningEffort = Array.isArray(model.capabilities?.supports?.reasoningEffort)
                ? model.capabilities?.supports?.reasoningEffort.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
                : [];

            return {
                id: typeof model.id === 'string' ? model.id.trim() : '',
                name: typeof model.name === 'string' ? model.name.trim() : '',
                reasoningEffortOptions: reasoningEffort,
            } satisfies AgencyModelPickerItem;
        })
        .filter(model => model.id.length > 0 && model.name.length > 0)
        .sort((left, right) => left.name.localeCompare(right.name));
}

export function formatTaskListScope(scope: string) {
    return scope
        .replaceAll(/\bOneDrive(?:And)?SharePoint\b/gi, 'ODSP')
        .replaceAll(/\bMicrosoft\b/gi, 'M')
        .replaceAll(/\bSharePoint\b/gi, 'S')
        .replaceAll(/\bAdministration\b/gi, 'A')
        .replaceAll(/\bUtilities\b/gi, 'U')
        .replaceAll(/\bVroom\b/gi, 'V');
}

export function buildDefaultTaskListDisplayName(serviceName: string, processName: string, taskTypeName: string) {
    return [serviceName, processName, taskTypeName]
        .map(part => part.trim())
        .filter(part => part.length > 0)
        .join(' ');
}

export function getTaskListDisplayName(taskListName?: string | null, taskListId?: number | null) {
    const trimmedName = taskListName?.trim();
    if (trimmedName) {
        return trimmedName;
    }

    if (taskListId !== undefined && taskListId !== null) {
        return `Task list ${taskListId}`;
    }

    return 'Task list';
}

export function buildTaskListDocumentTitle(taskListName?: string | null, generation?: number | null, taskListId?: number | null) {
    const displayName = getTaskListDisplayName(taskListName, taskListId);
    return generation !== undefined && generation !== null
        ? `${displayName} task list #${generation}`
        : displayName;
}

export function buildTaskDocumentTitle(taskListDisplayName: string, scopeName: string) {
    const trimmedScopeName = scopeName.trim();
    return trimmedScopeName ? `${taskListDisplayName} : ${trimmedScopeName}` : taskListDisplayName;
}

export function getArtifactTypeLabel(type: number) {
    switch (type) {
        case 0:
            return 'Job';
        case 1:
            return 'Skill';
        case 2:
            return 'Script';
        case 3:
            return 'Snippet';
        case 4:
            return 'Agent';
        case 5:
            return 'Plugin';
        case 6:
            return 'Pipeline';
        case 7:
            return 'Extraction';
        default:
            return `Type ${type}`;
    }
}

export function getJobStatusLabel(status?: number | null) {
    switch (status) {
        case 0:
            return 'Queued internally';
        case 1:
            return 'Queued';
        case 2:
            return 'Executing';
        case 3:
            return 'Succeeded';
        case 4:
            return 'Failed';
        case 5:
            return 'Pending answers';
        case 6:
            return 'Questions answered';
        case 7:
            return 'Submitting';
        default:
            return 'None';
    }
}

export function getPullRequestStatusLabel(status?: number | null) {
    switch (status) {
        case 0:
            return 'None';
        case 1:
            return 'In progress';
        case 2:
            return 'Draft';
        case 3:
            return 'Pending internal review';
        case 4:
            return 'Published';
        case 5:
            return 'Blocked';
        case 6:
            return 'Signed off';
        case 7:
            return 'Completed';
        case 8:
            return 'Abandoned';
        default:
            return 'None';
    }
}

export function getPullRequestHealthLabel(health?: number | null) {
    switch (health) {
        case 1:
            return 'Healthy';
        case 2:
            return 'Ongoing';
        case 3:
            return 'Expired';
        case 4:
            return 'Blocked';
        case 5:
            return 'Concurrency throttled';
        default:
            return 'Unknown';
    }
}

/** Describes the accepted Resolve action without implying that background assessment or repair has finished. */
export function getPullRequestRepairSuccessMessage(
    result: RepairAgentTaskPullRequestResult,
    prId?: number | null,
) {
    if (result.action === 2) {
        return prId
            ? `Guardian accepted the request and started a background validation recheck for PR ${prId}. Refresh later for updated status.`
            : 'Guardian accepted the request and started a background validation recheck. Refresh later for updated status.';
    }

    if (result.action === 1) {
        return prId
            ? `Guardian requeued the expired/throttled pipelines for PR ${prId}.`
            : 'Guardian requeued the expired/throttled pipelines.';
    }

    return prId
        ? `Queued PR ${prId} for background Agency repair.`
        : 'Queued the PR for background Agency repair.';
}

export function getPullRequestStatusWithHealthLabel(
    status?: number | null,
    health?: number | null,
    hasPullRequest = false,
) {
    if (hasPullRequest && status === 5) {
        return `Unknown (${getPullRequestHealthLabel(health === 0 || health == null ? 4 : health)})`;
    }

    const statusLabel = getPullRequestStatusLabel(status);
    if (!hasPullRequest || health === 1) {
        return statusLabel;
    }

    return `${statusLabel} (${getPullRequestHealthLabel(health)})`;
}

export function getFlagLabel(flags: number) {
    const labels: string[] = [];
    if ((flags & 1) !== 0) {
        labels.push('Orchestrator');
    }
    if ((flags & 2) !== 0) {
        labels.push('NoWork');
    }

    return labels.length > 0 ? labels.join(', ') : 'None';
}

/** Reads persisted UTC timestamps, including SQL responses that omit the offset. */
export function parseUtcDate(value?: string | null) {
    if (!value) return null;
    const date = new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`);

    return Number.isNaN(date.getTime()) ? null : date;
}

/** Displays UTC-backed timestamps locally; invalid and .NET minimum dates are unavailable. */
export function formatDate(value?: string | null, options?: Intl.DateTimeFormatOptions) {
    if (!value) return '-';

    const date = parseUtcDate(value);
    if (!date || date.toISOString() === '0001-01-01T00:00:00.000Z') return 'Unavailable';

    return date.toLocaleString(undefined, {
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        ...options,
    });
}

/** Displays a timestamp's local calendar day, distinct from a snapshot's calendar date. */
export function formatLocalDate(value?: string | null, options?: Intl.DateTimeFormatOptions) {
    if (!value) return '-';

    const date = parseUtcDate(value);
    if (!date || date.toISOString() === '0001-01-01T00:00:00.000Z') return 'Unavailable';

    return date.toLocaleDateString(undefined, options);
}

export function formatCompactDate(value?: string | null) {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString();
}

export function getSnapshotAgeInDays(snapshot?: AgentTaskSnapshotSummary | null) {
    if (!snapshot) {
        return undefined;
    }

    const snapshotDate = new Date(snapshot.date);
    if (Number.isNaN(snapshotDate.getTime())) {
        return undefined;
    }

    return Math.floor((Date.now() - snapshotDate.getTime()) / (24 * 60 * 60 * 1000));
}

export function buildAzureProfilerViewerUrl(sasUrl: string) {
    return `https://azprofilerclko.azurewebsites.net/ClickOnce/AzureProfilerViewer.application?Blob=${encodeURIComponent(window.btoa(sasUrl))}&NewPipeline=true`;
}

export function buildTaskCallGraphDownloadUrl(
    serviceName: string,
    taskListId: number,
    taskId: number,
    snapshotId: number,
    format: CallGraphDownloadFormat,
) {
    return `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/flamegraphfile?snapshotId=${snapshotId}&format=${format}`;
}

export function getDownloadFileName(contentDisposition: string | null, fallbackFileName: string) {
    const encodedFileNameMatch = contentDisposition?.match(/filename\*\s*=\s*([^;]+)/i);
    if (encodedFileNameMatch) {
        const encodedFileName = encodedFileNameMatch[1].trim().replace(/^"(.*)"$/, '$1').replace(/^UTF-8''/i, '');

        try {
            return decodeURIComponent(encodedFileName);
        } catch {
            return encodedFileName;
        }
    }

    const fileNameMatch = contentDisposition?.match(/filename\s*=\s*([^;]+)/i);
    if (fileNameMatch) {
        return fileNameMatch[1].trim().replace(/^"(.*)"$/, '$1');
    }

    return fallbackFileName;
}

export function triggerBrowserDownload(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export async function getDownloadErrorMessage(response: Response, fallbackMessage: string) {
    const responseText = await response.text();
    if (!responseText) {
        return fallbackMessage;
    }

    if ((response.headers.get('Content-Type') ?? '').toLowerCase().includes('application/json')) {
        try {
            const parsed = JSON.parse(responseText) as { message?: unknown };
            if (typeof parsed.message === 'string' && parsed.message.trim().length > 0) {
                return parsed.message;
            }
        } catch {
            return responseText;
        }
    }

    return responseText;
}

export function AuthenticatedDownloadLink({
    url,
    fallbackFileName,
    fallbackErrorMessage,
    children,
}: {
    url: string;
    fallbackFileName: string;
    fallbackErrorMessage: string;
    children: React.ReactNode;
}) {
    const [downloading, setDownloading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    async function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
        event.stopPropagation();
        if (downloading) {
            return;
        }

        setDownloading(true);
        setError(null);
        try {
            const response = await apiFetch(url);
            if (!response.ok) {
                throw new Error(await getDownloadErrorMessage(response, fallbackErrorMessage));
            }

            const blob = await response.blob();
            const fileName = getDownloadFileName(
                response.headers.get('Content-Disposition'),
                fallbackFileName,
            );
            triggerBrowserDownload(blob, fileName);
        } catch (err) {
            setError(err instanceof Error ? err.message : fallbackErrorMessage);
        } finally {
            setDownloading(false);
        }
    }

    return (
        <span>
            <button
                type="button"
                onClick={handleClick}
                onMouseDown={stopSelectionAndBubble}
                onMouseDownCapture={stopSelectionAndBubble}
                onPointerDown={stopSelectionAndBubble}
                onPointerDownCapture={stopSelectionAndBubble}
                data-selection-disabled={true}
                aria-busy={downloading}
                disabled={downloading}
                style={{
                    background: 'none',
                    border: 0,
                    color: '#005a9e',
                    cursor: downloading ? 'wait' : 'pointer',
                    font: 'inherit',
                    padding: 0,
                    textDecoration: 'underline',
                }}
            >
                {downloading ? 'Downloading...' : children}
            </button>
            {error && (
                <span role="alert" style={{ color: '#a4262c', marginLeft: 6 }}>
                    {error}
                </span>
            )}
        </span>
    );
}

export function formatNumber(value?: number | null, digits = 2) {
    if (value === undefined || value === null) {
        return '-';
    }

    return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    });
}

export function formatGranularity(value?: number | null) {
    if (value === undefined || value === null) {
        return '-';
    }

    return `${formatNumber(value * 100, 3)}%`;
}

export function parseOptionalInt(value: string | null) {
    if (!value) {
        return undefined;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseOptionalJobStatus(value: string | null) {
    const parsed = parseOptionalInt(value);
    return parsed !== undefined && parsed >= 0 && parsed <= 7 ? parsed : undefined;
}

export function parseOptionalPullRequestStatus(value: string | null) {
    const parsed = parseOptionalInt(value);
    return parsed !== undefined && parsed >= 0 && parsed <= 8 ? parsed : undefined;
}

export function parseOptionalTaskLifecycleStatus(value: string | null): AgentTaskLifecycleStatus | undefined {
    return value && AGENT_TASK_LIFECYCLE_STATUSES.includes(value as AgentTaskLifecycleStatus)
        ? value as AgentTaskLifecycleStatus
        : undefined;
}

export function getTaskLifecycleStatusLabel(status: AgentTaskLifecycleStatus) {
    switch (status) {
        case 'notStarted': return 'Not started';
        case 'analysisInProgress': return 'Analysis in progress';
        case 'needsInput': return 'Needs input';
        case 'analyzed': return 'Analyzed';
        case 'analyzedNoFix': return 'Analyzed - no fix';
        case 'readyToCode': return 'Ready to code';
        case 'codingInProgress': return 'Coding in progress';
        case 'prInProgress': return 'PR in progress';
        case 'prBlocked': return 'PR blocked';
        case 'done': return 'Done';
        case 'failed': return 'Failed';
        default: return 'Other';
    }
}

export function parseTaskListSortDirection(value: string | null): TaskListSortDirection | undefined {
    return value === 'asc' || value === 'desc' ? value : undefined;
}

export function isTaskListSortKey(value: string | null): value is TaskListSortKey {
    return !!value && TASK_LIST_SORT_KEYS.includes(value as TaskListSortKey);
}

export function updateSearchParams(
    searchParams: URLSearchParams,
    setSearchParams: (nextInit: URLSearchParams) => void,
    updates: Record<string, string | number | null | undefined>,
) {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
            next.delete(key);
        } else {
            next.set(key, String(value));
        }
    });

    setSearchParams(next);
}

export function buildJobDefinitionUrl(serviceName: string | undefined, jobName: string) {
    return `/service/${serviceName}/agenttasks/definitions?section=jobs&name=${encodeURIComponent(jobName)}`;
}

export function buildAgencySessionUrl(agencyJobId?: string | null) {
    return agencyJobId?.trim()
        ? `https://agency.microsoft.com/#session/${encodeURIComponent(agencyJobId.trim())}`
        : undefined;
}

export function buildWorkItemJobUrl(serviceName: string | undefined, jobName: string, agencyJobId?: string | null) {
    return buildAgencySessionUrl(agencyJobId) ?? buildJobDefinitionUrl(serviceName, jobName);
}

export function buildAgencyLogsDownloadUrl(serviceName: string, taskListId: number, taskId: number, workItemId: number) {
    return `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/workitems/${workItemId}/agency/logs`;
}

export function buildAgencyLogsArchiveDownloadUrl(serviceName: string, taskListId: number, taskId: number, workItemId: number) {
    return `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/workitems/${workItemId}/agency/logs/archive`;
}

export function buildAgencyLogsPageUrl(serviceName: string, taskListId: number, taskId: number, workItemId: number) {
    return `/service/${serviceName}/agenttasks/logs/${taskListId}/${taskId}/${workItemId}`;
}

export function buildAgencyLogsArchivePageUrl(serviceName: string, taskListId: number, taskId: number, workItemId: number) {
    return `/service/${serviceName}/agenttasks/archive/${taskListId}/${taskId}/${workItemId}`;
}

export function buildAgencyLogsArchiveIndexUrl(serviceName: string, taskListId: number, taskId: number, workItemId: number) {
    return `${buildAgencyLogsArchiveDownloadUrl(serviceName, taskListId, taskId, workItemId)}/index`;
}

export function buildAgencyLogsArchiveFileUrl(
    serviceName: string,
    taskListId: number,
    taskId: number,
    workItemId: number,
    path: string,
    download = false,
) {
    const baseUrl = `${buildAgencyLogsArchiveDownloadUrl(serviceName, taskListId, taskId, workItemId)}/file`;
    const search = new URLSearchParams({ path });
    if (download) search.set('download', 'true');
    return `${baseUrl}?${search.toString()}`;
}

export function stopSelectionAndBubble(event: { stopPropagation(): void }) {
    event.stopPropagation();
}

export function getServiceAzDoOrg(service: Service | undefined) {
    const candidate = service as (Service & { AzDOOrg?: string }) | undefined;
    return (service?.azDOOrg ?? candidate?.AzDOOrg)?.trim();
}

export function getServiceAdoProject(service: Service | undefined) {
    const candidate = service as (Service & { AgentTaskManager?: { AdoProject?: string } }) | undefined;
    return (service?.agentTaskManager?.adoProject ?? candidate?.AgentTaskManager?.AdoProject)?.trim();
}

export function getServiceDefaultRepoName(service: Service | undefined) {
    const candidate = service as (Service & { DefaultRepos?: { Name?: string } }) | undefined;
    return (service?.defaultRepos?.name ?? candidate?.DefaultRepos?.Name)?.trim();
}

export function getServiceDefaultRepoProject(service: Service | undefined) {
    const candidate = service as (Service & { DefaultRepos?: { Project?: string } }) | undefined;
    return (service?.defaultRepos?.project ?? candidate?.DefaultRepos?.Project)?.trim();
}

export function buildAzureDevOpsProjectUrl(service: Service | undefined, projectOverride?: string) {
    const org = getServiceAzDoOrg(service);
    const project = projectOverride?.trim() || getServiceAdoProject(service);
    if (!org || !project) {
        return undefined;
    }

    return `https://${org}.visualstudio.com/${encodeURIComponent(project)}`;
}

export function buildAdoWorkItemUrl(service: Service | undefined, adoId?: number | null) {
    if (adoId === undefined || adoId === null) {
        return undefined;
    }

    const projectUrl = buildAzureDevOpsProjectUrl(service);
    return projectUrl ? `${projectUrl}/_workitems/edit/${adoId}` : undefined;
}

export function buildAdoPullRequestUrl(service: Service | undefined, prId?: number | null) {
    if (prId === undefined || prId === null) {
        return undefined;
    }

    const projectUrl = buildAzureDevOpsProjectUrl(service, getServiceDefaultRepoProject(service));
    const repository = getServiceDefaultRepoName(service);
    if (!projectUrl || !repository) {
        return undefined;
    }

    return `${projectUrl}/_git/${encodeURIComponent(repository)}/pullrequest/${prId}`;
}

export function renderLinkedIdentifier(id: number | null | undefined, url: string | undefined) {
    if (id === undefined || id === null) {
        return '-';
    }

    return url ? (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-selection-disabled={true}
            onClick={stopSelectionAndBubble}
            onClickCapture={stopSelectionAndBubble}
            onMouseDown={stopSelectionAndBubble}
            onMouseDownCapture={stopSelectionAndBubble}
            onPointerDown={stopSelectionAndBubble}
            onPointerDownCapture={stopSelectionAndBubble}
            style={{ color: '#005a9e', textDecoration: 'underline' }}
        >
            {id}
        </a>
    ) : String(id);
}

export function renderPrefixedLinkedIdentifier(
    label: string,
    id: number | null | undefined,
    url: string | undefined,
) {
    if (id === undefined || id === null) {
        return null;
    }

    const text = `${label} ${id}`;
    return url ? (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-selection-disabled={true}
            onClick={stopSelectionAndBubble}
            onClickCapture={stopSelectionAndBubble}
            onMouseDown={stopSelectionAndBubble}
            onMouseDownCapture={stopSelectionAndBubble}
            onPointerDown={stopSelectionAndBubble}
            onPointerDownCapture={stopSelectionAndBubble}
            style={{ color: '#005a9e', textDecoration: 'underline' }}
        >
            {text}
        </a>
    ) : <span>{text}</span>;
}

export function getDefaultTaskListSortDirection(sortKey: TaskListSortKey): TaskListSortDirection {
    switch (sortKey) {
        case 'scope':
        case 'jobStatus':
        case 'pullRequestStatus':
            return 'asc';
        default:
            return 'desc';
    }
}

export function compareNumbers(left: number, right: number) {
    if (left < right) {
        return -1;
    }
    if (left > right) {
        return 1;
    }

    return 0;
}

export function compareStrings(left: string, right: string) {
    return left.localeCompare(right, undefined, { sensitivity: 'base' });
}

export function sortWorkItemsForSummary(workItems: AgentTaskWorkItemSummary[]) {
    return [...workItems].sort((left, right) => {
        return compareNumbers(right.workItemId, left.workItemId);
    });
}

export function getLatestWorkItem(workItems: AgentTaskWorkItemSummary[]) {
    return workItems.reduce<AgentTaskWorkItemSummary | undefined>(
        (latest, workItem) => (!latest || workItem.workItemId > latest.workItemId ? workItem : latest),
        undefined,
    );
}

export function getLatestWorkItemWithAdoId(workItems: AgentTaskWorkItemSummary[]) {
    return [...workItems]
        .sort((left, right) => compareNumbers(right.workItemId, left.workItemId))
        .find(workItem => workItem.adoId !== undefined && workItem.adoId !== null);
}

export function decorateLatestWorkItemForSummary(
    latestWorkItem: AgentTaskWorkItemSummary | undefined,
    sourceWorkItems: AgentTaskWorkItemSummary[],
    repairWorkItems: AgentTaskWorkItemSummary[] = [],
) {
    if (!latestWorkItem) {
        return undefined;
    }

    const latestAdoReference = getLatestWorkItemWithAdoId(sourceWorkItems)?.adoId;
    const sourcePullRequestIds = new Set(
        sourceWorkItems
            .map(workItem => workItem.prId)
            .filter((prId): prId is number => prId !== undefined && prId !== null),
    );
    const matchingRepairWorkItems = repairWorkItems
        .filter(workItem =>
            workItem.workItemKind === 1
            && workItem.prId !== undefined
            && workItem.prId !== null
            && sourcePullRequestIds.has(workItem.prId))
        .sort((left, right) => compareNumbers(right.workItemId, left.workItemId));
    const latestRepairWorkItem = matchingRepairWorkItems[0];
    return {
        ...latestWorkItem,
        adoId: latestWorkItem.adoId ?? latestAdoReference,
        latestRepairWorkItem,
        repairWorkItems: matchingRepairWorkItems,
    };
}

export function getLatestSummaryWorkItemForJob(workItems: AgentTaskWorkItemSummary[], jobName: string) {
    const matchingWorkItems = workItems.filter(workItem => workItem.jobName === jobName);
    const sourceWorkItems = matchingWorkItems.filter(workItem => workItem.workItemKind === 0);
    if (sourceWorkItems.length === 0) {
        return [];
    }

    const latestWorkItem = decorateLatestWorkItemForSummary(
        getLatestWorkItem(sourceWorkItems),
        sourceWorkItems,
        matchingWorkItems.filter(workItem => workItem.workItemKind === 1),
    );
    if (!latestWorkItem) {
        return [];
    }

    return [latestWorkItem];
}

export function taskHasWorkItemForJob(task: AgentTaskTaskSummary, selectedJobName?: string) {
    return !selectedJobName || task.workItems.some(workItem => workItem.jobName === selectedJobName);
}

export function synthesizeTaskSummaryWorkItems(workItems: AgentTaskWorkItemSummary[], selectedJobName?: string) {
    if (!workItems.length) {
        return [];
    }

    if (selectedJobName) {
        return sortWorkItemsForSummary(getLatestSummaryWorkItemForJob(workItems, selectedJobName));
    }

    const repairWorkItems = workItems.filter(workItem => workItem.workItemKind === 1);
    const groupedWorkItems = new Map<string, AgentTaskWorkItemSummary[]>();
    for (const workItem of [...workItems]
        .filter(item => item.workItemKind === 0)
        .sort((left, right) => compareNumbers(right.workItemId, left.workItemId))) {
        const existing = groupedWorkItems.get(workItem.jobName);
        if (existing) {
            existing.push(workItem);
        } else {
            groupedWorkItems.set(workItem.jobName, [workItem]);
        }
    }

    const summaryItems: AgentTaskWorkItemSummary[] = [];
    groupedWorkItems.forEach(jobWorkItems => {
        const latestWorkItem = decorateLatestWorkItemForSummary(
            getLatestWorkItem(jobWorkItems),
            jobWorkItems,
            repairWorkItems,
        );
        if (latestWorkItem) {
            summaryItems.push(latestWorkItem);
        }
    });

    return sortWorkItemsForSummary(summaryItems);
}

export function synthesizeLatestJobExecutions(
    workItems: AgentTaskWorkItemSummary[],
    selectedJobName?: string,
) {
    const latestByJob = new Map<string, AgentTaskWorkItemSummary>();
    for (const workItem of workItems) {
        if (selectedJobName && workItem.jobName !== selectedJobName) {
            continue;
        }

        const latest = latestByJob.get(workItem.jobName);
        if (!latest || workItem.workItemId > latest.workItemId) {
            latestByJob.set(workItem.jobName, workItem);
        }
    }

    return sortWorkItemsForSummary([...latestByJob.values()]);
}

export function collectDisplayValues(workItems: AgentTaskWorkItemSummary[], selector: (workItem: AgentTaskWorkItemSummary) => number) {
    const values: number[] = [];
    const seen = new Set<number>();
    for (const workItem of workItems) {
        const value = selector(workItem);
        if (!seen.has(value)) {
            seen.add(value);
            values.push(value);
        }
    }

    return values;
}

export function getJobStatusSortValue(status?: number | null) {
    switch (status) {
        case 4:
            return 0;
        case 2:
            return 1;
        case 7:
            return 1;
        case 1:
            return 2;
        case 0:
            return 3;
        case 3:
            return 4;
        default:
            return 5;
    }
}

export function getTaskSummaryJobStatus(workItem: AgentTaskWorkItemSummary) {
    const repairStatus = workItem.latestRepairWorkItem?.jobStatus;
    return repairStatus === 0 || repairStatus === 1 || repairStatus === 2 || repairStatus === 7
        ? repairStatus
        : workItem.jobStatus;
}

export function getPullRequestStatusSortValue(status?: number | null) {
    switch (status) {
        case 5:
            return 0;
        case 3:
            return 1;
        case 2:
            return 2;
        case 4:
            return 3;
        case 6:
            return 4;
        case 1:
            return 5;
        case 7:
            return 6;
        case 8:
            return 7;
        case 0:
            return 8;
        default:
            return 9;
    }
}

export function getTaskLifecycleStatus(
    task: Pick<AgentTaskTaskSummary, 'workItems' | 'pullRequests'>
        & Partial<Pick<AgentTaskTaskSummary,
            'activeProposalCount' | 'votedProposalCount' | 'okProposalCount' | 'hasStructuredAnalysis'>>,
): AgentTaskLifecycleStatus {
    const workItems = task.workItems ?? [];
    const pullRequests = task.pullRequests ?? [];
    if (pullRequests.some(pullRequest => pullRequest.status === 7)) {
        return 'done';
    }

    const currentPullRequests = pullRequests.filter(pullRequest =>
        pullRequest.status > 0 && pullRequest.status < 7);
    if (currentPullRequests.some(pullRequest => pullRequest.status === 5 || pullRequest.health === 4)) {
        return 'prBlocked';
    }
    if (currentPullRequests.length > 0) {
        return 'prInProgress';
    }

    const latestAnalysis = getLatestWorkItem(workItems.filter(workItem => !workItem.canCreatePR));
    const latestCoding = getLatestWorkItem(workItems.filter(workItem => workItem.canCreatePR));
    const latestRelevant = !latestAnalysis || (latestCoding?.workItemId ?? 0) > latestAnalysis.workItemId
        ? latestCoding
        : latestAnalysis;
    if (latestRelevant?.jobStatus === 5) {
        return 'needsInput';
    }
    if (latestRelevant && [0, 1, 2, 7].includes(latestRelevant.jobStatus)) {
        return latestRelevant.canCreatePR ? 'codingInProgress' : 'analysisInProgress';
    }
    if (latestRelevant?.jobStatus === 4 && pullRequests.length === 0) {
        return 'failed';
    }

    const latestAnalysisSucceeded = latestAnalysis?.jobStatus === 3;
    const analysisIsNewestAttempt = !latestCoding || (latestAnalysis?.workItemId ?? 0) > latestCoding.workItemId;
    if (latestAnalysisSucceeded && analysisIsNewestAttempt) {
        const activeProposalCount = task.activeProposalCount;
        const votedProposalCount = task.votedProposalCount;
        const okProposalCount = task.okProposalCount;
        if (activeProposalCount !== undefined
            && votedProposalCount !== undefined
            && okProposalCount !== undefined) {
            if (activeProposalCount === votedProposalCount && okProposalCount > 0) {
                return 'readyToCode';
            }
            if (task.hasStructuredAnalysis === true
                && (activeProposalCount === 0
                || (activeProposalCount === votedProposalCount && okProposalCount === 0))) {
                return 'analyzedNoFix';
            }
        }
        return 'analyzed';
    }

    return workItems.length === 0 ? 'notStarted' : 'other';
}

export function buildTaskListRowModel(task: AgentTaskTaskSummary, selectedJobName?: string): AgentTaskListRowModel {
    const displayWorkItems = synthesizeTaskSummaryWorkItems(task.workItems, selectedJobName);
    const jobSummaryWorkItems = synthesizeLatestJobExecutions(task.workItems);
    const selectedJobExecutions = selectedJobName
        ? synthesizeLatestJobExecutions(task.workItems, selectedJobName)
        : jobSummaryWorkItems;
    const displayPullRequests = task.pullRequests ?? [];

    return {
        key: task.taskId,
        task,
        displayWorkItems,
        jobSummaryWorkItems,
        displayPullRequests,
        displayJobStatuses: collectDisplayValues(
            selectedJobExecutions,
            workItem => workItem.jobStatus,
        ),
        displayPullRequestStatuses: Array.from(new Set(displayPullRequests.map(pullRequest => pullRequest.status))),
        primaryJobStatus: selectedJobExecutions[0]?.jobStatus,
        primaryPullRequestStatus: displayPullRequests[0]?.status,
        latestDisplayWorkItemId: getLatestWorkItem(jobSummaryWorkItems)?.workItemId ?? 0,
        taskStatus: getTaskLifecycleStatus(task),
    };
}

export function compareTaskListRows(
    left: AgentTaskListRowModel,
    right: AgentTaskListRowModel,
    sortBy: TaskListSortKey,
    sortDirection: TaskListSortDirection,
) {
    let comparison = 0;
    switch (sortBy) {
        case 'scope':
            comparison = compareStrings(left.task.scope, right.task.scope);
            break;
        case 'inclusive':
            comparison = compareNumbers(left.task.inclusive, right.task.inclusive);
            break;
        case 'exclusive':
            comparison = compareNumbers(left.task.exclusive, right.task.exclusive);
            break;
        case 'methodCount':
            comparison = compareNumbers(left.task.methodCount, right.task.methodCount);
            break;
        case 'subTaskCount':
            comparison = compareNumbers(left.task.subTaskCount, right.task.subTaskCount);
            break;
        case 'depth':
            comparison = compareNumbers(left.task.depth, right.task.depth);
            break;
        case 'jobStatus':
            comparison = compareNumbers(getJobStatusSortValue(left.primaryJobStatus), getJobStatusSortValue(right.primaryJobStatus));
            break;
        case 'pullRequestStatus':
            comparison = compareNumbers(getPullRequestStatusSortValue(left.primaryPullRequestStatus), getPullRequestStatusSortValue(right.primaryPullRequestStatus));
            break;
        case 'workItems':
            comparison = compareNumbers(left.jobSummaryWorkItems.length, right.jobSummaryWorkItems.length)
                || compareNumbers(left.latestDisplayWorkItemId ?? 0, right.latestDisplayWorkItemId ?? 0);
            break;
    }

    if (comparison !== 0) {
        return sortDirection === 'desc' ? -comparison : comparison;
    }

    return compareNumbers(right.task.inclusive, left.task.inclusive)
        || compareNumbers(left.task.taskId, right.task.taskId);
}

export function renderStatusValues(statuses: number[], getLabel: (status?: number | null) => string) {
    if (statuses.length === 0) {
        return 'None';
    }

    if (statuses.length === 1) {
        return getLabel(statuses[0]);
    }

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            {statuses.map(status => (
                <span key={status}>{getLabel(status)}</span>
            ))}
        </Stack>
    );
}

export function renderTaskJobStatuses(
    _service: Service | undefined,
    workItems: AgentTaskWorkItemSummary[] | undefined,
) {
    if (!workItems?.length) {
        return 'None';
    }

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            {workItems.map(workItem => (
                <span key={workItem.workItemId}>
                    {getJobStatusLabel(getTaskSummaryJobStatus(workItem))}
                </span>
            ))}
        </Stack>
    );
}

export function formatJobExecutionMonthDay(value?: string | null) {
    return value ? formatLocalDate(value, {
        month: 'numeric',
        day: 'numeric',
    }) : undefined;
}

export function renderTaskJobs(workItems: AgentTaskWorkItemSummary[] | undefined) {
    if (!workItems?.length) {
        return '-';
    }

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            {workItems.map(workItem => {
                const status = workItem.jobStatus;
                const executionDate = formatJobExecutionMonthDay(
                    workItem.agentEndDate ?? workItem.agentStartDate,
                );
                const color = status === 3
                    ? '#107c10'
                    : status === 4
                        ? '#a4262c'
                        : status === 5
                            ? '#8a6d00'
                            : status === 0 || status === 1 || status === 2 || status === 7
                                ? '#005a9e'
                                : '#605e5c';

                return (
                    <span
                        key={workItem.workItemId}
                        aria-label={`${workItem.jobName}${executionDate ? ` (${executionDate})` : ''}: ${getJobStatusLabel(status)}`}
                        title={getJobStatusLabel(status)}
                        style={{ color, lineHeight: 1.3 }}
                    >
                        {workItem.jobName}
                        {executionDate && ` (${executionDate})`}
                    </span>
                );
            })}
        </Stack>
    );
}

export function renderTaskSummaryWorkItems(
    service: Service | undefined,
    workItems: AgentTaskWorkItemSummary[] | undefined,
    taskListId?: number,
    taskId?: number,
    currentSnapshotId?: number | null,
) {
    if (!workItems?.length) {
        return '-';
    }

    return (
        <Stack tokens={{ childrenGap: 6 }}>
            {workItems.map(workItem => {
                const references: React.ReactNode[] = [];
                const adoReference = renderPrefixedLinkedIdentifier('ADO', workItem.adoId, buildAdoWorkItemUrl(service, workItem.adoId));
                const showSnapshotId = currentSnapshotId === undefined || currentSnapshotId === null || workItem.snapshotId !== currentSnapshotId;
                const statusTimestamp = workItem.jobStatus === 2
                    ? workItem.agentStartDate
                    : [3, 4, 5, 6].includes(workItem.jobStatus)
                        ? workItem.agentEndDate ?? workItem.agentStartDate
                        : null;
                const questionCount = (workItem.openQuestionCount ?? 0)
                    + (workItem.answeredQuestionCount ?? 0)
                    + (workItem.declinedQuestionCount ?? 0);
                if (adoReference) {
                    references.push(adoReference);
                }
                if (service?.name && taskListId !== undefined && taskId !== undefined) {
                    if (questionCount > 0) {
                        references.push(
                            <a
                                href={buildAgentTaskQuestionsPageUrl(service.name, {
                                    taskListId,
                                    taskId,
                                    workItemId: workItem.workItemId,
                                })}
                                data-selection-disabled={true}
                                onClick={stopSelectionAndBubble}
                                onClickCapture={stopSelectionAndBubble}
                                onMouseDown={stopSelectionAndBubble}
                                onMouseDownCapture={stopSelectionAndBubble}
                                onPointerDown={stopSelectionAndBubble}
                                onPointerDownCapture={stopSelectionAndBubble}
                            >
                                Questions
                            </a>,
                        );
                    }
                    references.push(
                        <a
                            href={buildAgentTaskProposedChangesPageUrl(service.name, {
                                taskListId,
                                taskId,
                                workItemId: workItem.workItemId,
                            })}
                            data-selection-disabled={true}
                            onClick={stopSelectionAndBubble}
                            onClickCapture={stopSelectionAndBubble}
                            onMouseDown={stopSelectionAndBubble}
                            onMouseDownCapture={stopSelectionAndBubble}
                            onPointerDown={stopSelectionAndBubble}
                            onPointerDownCapture={stopSelectionAndBubble}
                        >
                            Changes
                        </a>,
                    );
                }

                return (
                    <div key={workItem.workItemId} style={{ lineHeight: 1.3 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                            <a
                                href={buildWorkItemJobUrl(service?.name, workItem.jobName, workItem.agencyJobId)}
                                target={workItem.agencyJobId ? '_blank' : undefined}
                                rel={workItem.agencyJobId ? 'noopener noreferrer' : undefined}
                                data-selection-disabled={true}
                                onClick={stopSelectionAndBubble}
                                onClickCapture={stopSelectionAndBubble}
                                onMouseDown={stopSelectionAndBubble}
                                onMouseDownCapture={stopSelectionAndBubble}
                                onPointerDown={stopSelectionAndBubble}
                                onPointerDownCapture={stopSelectionAndBubble}
                                style={{ color: '#005a9e', textDecoration: 'underline' }}
                            >
                                {workItem.jobName}
                            </a>
                            {statusTimestamp && ` (${formatDate(statusTimestamp)})`}
                        </div>
                        <div style={{ fontSize: 12, color: '#605e5c' }}>
                            {showSnapshotId && (
                                <>
                                    <span>{`Snapshot #${workItem.snapshotId}`}</span>
                                    {references.length > 0 && ' • '}
                                </>
                            )}
                            {references.length > 0 && (
                                <>
                                    {references.map((reference, index) => (
                                        <React.Fragment key={`${workItem.workItemId}-${index}`}>
                                            {index > 0 && ' • '}
                                            {reference}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </Stack>
    );
}

export function containsSnippetSyntax(text?: string | null) {
    return !!text && SNIPPET_SYNTAX_REGEX.test(text);
}

export function getSnippetArtifactValidationError(text?: string | null) {
    if (!text) {
        return null;
    }

    for (const match of text.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) {
        const token = (match[1] ?? '').trim();
        if (!token) {
            return 'Snippet placeholders must include a token name.';
        }

        if (token.includes('.')) {
            if (!SUPPORTED_RUNTIME_SUBSTITUTION_TOKENS.has(token.toLowerCase())) {
                return `Unsupported runtime substitution '${token}'.`;
            }

            continue;
        }

        return `Snippets can contain runtime substitution placeholders, but cannot reference other snippets like '{{ ${token} }}'. Nested snippets are not supported.`;
    }

    return null;
}

export function buildSubstitutionToken(token: string) {
    return `{{ ${token} }}`;
}

export function buildSnippetToken(snippetName: string) {
    return buildSubstitutionToken(snippetName);
}

export function insertSubstitutionReference(text: string, start: number, end: number, tokenName: string) {
    const token = buildSubstitutionToken(tokenName);
    const before = text.slice(0, start);
    const after = text.slice(end);
    const nextText = `${before}${token}${after}`;
    const nextSelection = (before + token).length;

    return {
        text: nextText,
        selectionStart: nextSelection,
        selectionEnd: nextSelection,
    };
}

export function insertSnippetReference(text: string, start: number, end: number, snippetName: string) {
    const token = buildSnippetToken(snippetName);
    const before = text.slice(0, start);
    const after = text.slice(end);
    const prefix = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
    const suffix = after.length > 0 && !after.startsWith('\n') ? '\n' : '';
    const nextText = `${before}${prefix}${token}${suffix}${after}`;
    const nextSelection = (before + prefix + token).length;

    return {
        text: nextText,
        selectionStart: nextSelection,
        selectionEnd: nextSelection,
    };
}

export function buildFlameGraphTree(result: AgentTaskFlameGraphResult) {
    const nodes = result.nodes.map(node => ({
        ...node,
        children: [] as AgentTaskFlameGraphNode[],
    }));

    const byId = new Map<number, AgentTaskFlameGraphNode>();
    nodes.forEach(node => byId.set(node.id, node));

    let root = nodes[0];
    nodes.forEach(node => {
        if (node.parentId >= 0) {
            const parent = byId.get(node.parentId);
            if (parent) {
                parent.children = parent.children ?? [];
                parent.children.push(node);
            }
        } else {
            root = node;
        }
    });

    return root;
}

export async function submitMultipartRequest<TResponse>(
    url: string,
    fields: Record<string, string | number | undefined>,
    file?: File | null,
    parseErrorResponse?: false,
): Promise<TResponse>;
export async function submitMultipartRequest<TResponse>(
    url: string,
    fields: Record<string, string | number | undefined>,
    file: File | null | undefined,
    parseErrorResponse: true,
): Promise<TResponse | MultipartRequestError>;
export async function submitMultipartRequest<TResponse>(
    url: string,
    fields: Record<string, string | number | undefined>,
    file?: File | null,
    parseErrorResponse = false,
) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            formData.append(key, String(value));
        }
    });

    if (file) {
        formData.append('file', file);
    }

    const response = await apiFetch(url, { method: 'POST', body: formData });
    if (parseErrorResponse) {
        const responseData = await response.json().catch(() => null);
        if (response.ok) {
            return responseData as TResponse;
        }

        return { data: responseData, status: response.status, error: true } as MultipartRequestError;
    }

    if (!response.ok) {
        const body = await response.text().catch(() => null);
        throw new Error(body || `Request failed with status ${response.status}`);
    }

    return response.json() as Promise<TResponse>;
}
